/**
 * Page de gestion des catégories
 * CRUD complet avec arborescence et icônes emoji
 */

import { useState, useEffect, type CSSProperties } from 'react';
import { api } from '../services/api';

interface Category {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  icon?: string | null;
  parent_id?: string | null;
  active: boolean;
  created_at: string;
  updated_at: string;
}

// Emojis suggérés pour les catégories (sans doublons)
const EMOJI_SUGGESTIONS = [
  '🥕', '🍎', '🍞', '🧀', '🍷', '🥛', '🍖', '🐟', '🥗', '🍰',
  '🫒', '🥦', '🍊', '🍌', '🍇', '🥒', '🍅', '🥔', '🧅', '🌽',
  '🥬', '🫑', '🍠', '🍆', '🥜', '🌰', '🫘', '🫚', '🧄', '🍄',
  '🥐', '🥖', '🥨', '🥯', '🧇', '🥞', '🧈', '🍯', '🍺', '🥂',
  '🧃', '☕', '🧋', '🫖', '🧊', '🥤', '🧉', '🏪', '🛒', '📦',
  '🎁', '🏷️', '💰', '🍴', '🥄', '🔪', '🥢', '🥩', '🫔', '🧁',
];

export default function Categories() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: '',
    parent_id: '',
    active: true,
  });
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const data = await api.getCategories();
      setCategories(data);
    } catch (error) {
      console.error('Erreur chargement catégories:', error);
      alert('Erreur lors du chargement des catégories');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const data = {
        name: formData.name,
        description: formData.description || undefined,
        icon: formData.icon || undefined,
        parent_id: formData.parent_id || undefined,
        active: formData.active,
      };

      if (editingId) {
        await api.updateCategory(editingId, data);
        alert('Catégorie modifiée avec succès');
      } else {
        await api.createCategory(data);
        alert('Catégorie créée avec succès');
      }

      setFormData({ name: '', description: '', icon: '', parent_id: '', active: true });
      setEditingId(null);
      loadCategories();
    } catch (error: any) {
      console.error('Erreur:', error);
      alert(error.response?.data?.message || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleEdit = (category: Category) => {
    setEditingId(category.id);
    setFormData({
      name: category.name,
      description: category.description || '',
      icon: category.icon || '',
      parent_id: category.parent_id || '',
      active: category.active,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette catégorie ?')) return;

    try {
      await api.deleteCategory(id);
      alert('Catégorie supprimée avec succès');
      loadCategories();
    } catch (error: any) {
      console.error('Erreur suppression:', error);
      alert(error.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const handleCancel = () => {
    setFormData({ name: '', description: '', icon: '', parent_id: '', active: true });
    setEditingId(null);
  };

  const handleSelectEmoji = (emoji: string) => {
    setFormData({ ...formData, icon: emoji });
    setShowEmojiPicker(false);
  };

  // Organiser les catégories en arbre
  const rootCategories = categories.filter((c) => !c.parent_id);
  const getChildren = (parentId: string) =>
    categories.filter((c) => c.parent_id === parentId);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Catégories</h1>
        <p className="mt-1 text-sm text-gray-500">
          Gestion des catégories de produits ({categories.length} catégories)
        </p>
      </div>

      <div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Form */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow p-6 sticky top-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                {editingId ? 'Modifier' : 'Nouvelle'} catégorie
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="ex: Fruits & Légumes"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Icône (emoji)
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={formData.icon}
                      onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-2xl text-center"
                      placeholder="🥕"
                      maxLength={2}
                    />
                    <button
                      type="button"
                      onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                      className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      {showEmojiPicker ? '✕' : '😊'}
                    </button>
                  </div>

                  {/* Emoji Picker */}
                  {showEmojiPicker && (
                    <div className="mt-2 p-3 border border-gray-300 rounded-md bg-gray-50 max-h-40 overflow-y-auto">
                      <div className="grid grid-cols-8 gap-1">
                        {EMOJI_SUGGESTIONS.map((emoji, index) => (
                          <button
                            key={`${emoji}-${index}`}
                            type="button"
                            onClick={() => handleSelectEmoji(emoji)}
                            className="text-2xl hover:bg-blue-100 rounded p-1 transition-colors"
                            title={emoji}
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="ex: Produits frais de saison"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Catégorie parente
                  </label>
                  <select
                    value={formData.parent_id}
                    onChange={(e) => setFormData({ ...formData, parent_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Aucune (catégorie racine)</option>
                    {rootCategories.map((cat) => (
                      <option key={cat.id} value={cat.id} disabled={cat.id === editingId}>
                        {cat.icon} {cat.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900">Active</label>
                </div>

                <div className="flex gap-2 pt-2">
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
                  >
                    {editingId ? '✓ Modifier' : '+ Créer'}
                  </button>
                  {editingId && (
                    <button
                      type="button"
                      onClick={handleCancel}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                    >
                      Annuler
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>

          {/* List */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
                </div>
              ) : categories.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">📂</div>
                  <p className="text-xl font-medium text-gray-900 mb-2">Aucune catégorie</p>
                  <p className="text-sm text-gray-500">Créez votre première catégorie pour organiser vos produits</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {rootCategories.map((category) => (
                    <CategoryRow
                      key={category.id}
                      category={category}
                      children={getChildren(category.id)}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      level={0}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Component pour afficher une catégorie avec ses enfants
interface CategoryRowProps {
  category: Category;
  children: Category[];
  onEdit: (category: Category) => void;
  onDelete: (id: string) => void;
  level: number;
}

function CategoryRow({ category, children, onEdit, onDelete, level }: CategoryRowProps) {
  const [expanded, setExpanded] = useState(true);
  // Retrait par niveau : 40 px sur ordinateur, 16 sur téléphone — au troisième
  // niveau, 104 px de marge ne laissaient plus de place aux boutons (23/10/2026).
  return (
    <>
      <div
        className="px-4 sm:px-6 py-4 hover:bg-gray-50 pl-[calc(1rem+var(--niveau)*1rem)] sm:pl-[calc(1.5rem+var(--niveau)*2.5rem)]"
        style={{ ['--niveau' as string]: level } as CSSProperties}
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {children.length > 0 && (
              <button
                onClick={() => setExpanded(!expanded)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg
                  className={`w-5 h-5 transition-transform ${expanded ? 'rotate-90' : ''}`}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            )}

            {/* Icon */}
            {category.icon && (
              <div className="text-3xl">{category.icon}</div>
            )}

            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-base font-medium text-gray-900">{category.name}</span>
                <span
                  className={`px-2 py-1 text-xs rounded-full ${
                    category.active
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {category.active ? 'Active' : 'Inactive'}
                </span>
              </div>
              {category.description && (
                <p className="mt-1 text-sm text-gray-500">{category.description}</p>
              )}
              <p className="mt-1 text-xs text-gray-400">Slug: {category.slug}</p>
            </div>
          </div>

          <div className="flex items-center gap-2 ml-auto">
            <button
              onClick={() => onEdit(category)}
              className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded font-medium"
            >
              ✏️ Modifier
            </button>
            <button
              onClick={() => onDelete(category.id)}
              className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded font-medium"
            >
              🗑️ Supprimer
            </button>
          </div>
        </div>
      </div>

      {expanded &&
        children.map((child) => (
          <CategoryRow
            key={child.id}
            category={child}
            children={[]}
            onEdit={onEdit}
            onDelete={onDelete}
            level={level + 1}
          />
        ))}
    </>
  );
}
